import { useRequest } from 'ahooks'
import { Collapse, CollapsePanelProps, List, Progress, Tooltip, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { StageCompleter } from '@/components'
import { routes } from '@/router'
import { MyProjectStage } from '@/schemas'
import { ga, traceEvent } from '@/utils/ga'

import { PlanDate } from './plan-date.component'
import { Switch } from './switch.component'

enum StageType {
  OWNER = 1,
  PARTICIPATOR = 2,
}

const { Text } = Typography

export const MyStages: React.FC<CollapsePanelProps> = (props) => {
  const { t } = useTranslation()

  const [stageType, setStageType] = React.useState(StageType.OWNER)

  const { data, refresh } = useRequest<MyProjectStage[]>(
    {
      url: '/api/project/getMyProjectStages',
      params: {
        stageType,
      },
    },
    {
      refreshDeps: [stageType],
    }
  )

  const options = [
    { label: t('owner'), value: StageType.OWNER, name: '负责' },
    { label: t('join in'), value: StageType.PARTICIPATOR, name: '参与' },
  ]

  const onChange = (searchType: StageType) => {
    setStageType(searchType)
  }

  const header = (
    <>
      <span>{props.header}</span>
      <Switch options={options} value={stageType} onChange={onChange} />
    </>
  )

  return (
    <Collapse.Panel {...props} header={header}>
      <List
        className='max-h-600px overflow-y-auto'
        itemLayout='horizontal'
        loading={!data}
        size='small'
        bordered
        locale={{ emptyText: <div className='text-left'>{t('personal.mystage.tip')}</div> }}
        dataSource={data || []}
        renderItem={(s) => (
          <List.Item className='space-x-24px'>
            <div className='flex-grow flex'>
              <span>
                <StageCompleter stage={s} onSuccess={refresh} />
              </span>

              <Text ellipsis className='w-full !max-w-180px'>
                <Link
                  to={routes.project.page(s.selfId)}
                  onClick={() => {
                    ga('event', traceEvent.personalPage.events.click_check_detail, {
                      event_category: traceEvent.personalPage.event_categories.ef_pmo_home_my_project_stages,
                      action: traceEvent.personalPage.action,
                      event_label: s.project.name,
                      target: traceEvent.personalPage.targets.ef_pmo_project_detail,
                    })

                    window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_home)
                  }}
                >
                  {s.project.name}
                </Link>
              </Text>

              <Text ellipsis className='mr-24px'>
                <Link
                  to={routes.project.page(s.selfId, { tab: 'stage' })}
                  onClick={() => {
                    ga('event', traceEvent.personalPage.events.click_check_detail, {
                      event_category: traceEvent.personalPage.event_categories.ef_pmo_home_my_project_stages,
                      action: traceEvent.personalPage.action,
                      event_label: s.name,
                      target: traceEvent.personalPage.targets.ef_pmo_project_detail,
                    })

                    window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_home)
                  }}
                >
                  {s.name}
                </Link>
              </Text>
            </div>

            <Tooltip title={t('progress')}>
              <Progress className='flex-shrink-0 self-end' type='circle' percent={s.progress} width={23} />
            </Tooltip>

            <PlanDate date={s.planEndDate} tooltip={t('plan end date')} />
          </List.Item>
        )}
      />
    </Collapse.Panel>
  )
}
